// =============================================================================
// CURRENCIES, the list shown in every dropdown (Settings, holdings, accounts).
// =============================================================================
// Coverage philosophy (Phase 38):
//   • The FX engine (open.er-api.com) supports ~166 currencies, any code
//     that's a valid ISO-4217 will resolve a rate at runtime via lazy fetch.
//   • This list is purely the user-facing PICKER. We include the codes our
//     target audience is most likely to need, grouped by region:
//       - India + neighbors (where the bulk of users live or have origin)
//       - GCC / Middle East (the diaspora cluster)
//       - North America / UK / Eurozone
//       - APAC majors
//       - Africa + Latin America (selective, most-traded)
//   • If a user types a holding in some other currency, the dropdown won't
//     offer it today, they'd need us to add it here, or we add a free-text
//     ISO input later. Easy follow-up.
// =============================================================================
export const CURRENCIES = [
  // Home market + neighbors
  { code: 'INR', symbol: '₹',    label: 'Indian Rupee',         locale: 'en-IN' },
  { code: 'NPR', symbol: 'NPR ', label: 'Nepalese Rupee',       locale: 'en-NP' },
  { code: 'LKR', symbol: 'LKR ', label: 'Sri Lankan Rupee',     locale: 'en-LK' },
  { code: 'BDT', symbol: 'BDT ', label: 'Bangladeshi Taka',     locale: 'en-BD' },
  { code: 'PKR', symbol: 'PKR ', label: 'Pakistani Rupee',      locale: 'en-PK' },
  // GCC + Middle East (diaspora belt)
  { code: 'AED', symbol: 'AED ', label: 'UAE Dirham',           locale: 'en-AE' },
  { code: 'SAR', symbol: 'SAR ', label: 'Saudi Riyal',          locale: 'en-SA' },
  { code: 'QAR', symbol: 'QAR ', label: 'Qatari Riyal',         locale: 'en-QA' },
  { code: 'KWD', symbol: 'KWD ', label: 'Kuwaiti Dinar',        locale: 'en-KW' },
  { code: 'OMR', symbol: 'OMR ', label: 'Omani Rial',           locale: 'en-OM' },
  { code: 'BHD', symbol: 'BHD ', label: 'Bahraini Dinar',       locale: 'en-BH' },
  { code: 'JOD', symbol: 'JOD ', label: 'Jordanian Dinar',      locale: 'en-JO' },
  { code: 'ILS', symbol: '₪',    label: 'Israeli Shekel',       locale: 'he-IL' },
  { code: 'TRY', symbol: '₺',    label: 'Turkish Lira',         locale: 'tr-TR' },
  { code: 'IRR', symbol: 'IRR ', label: 'Iranian Rial',         locale: 'fa-IR' },
  { code: 'IQD', symbol: 'IQD ', label: 'Iraqi Dinar',          locale: 'ar-IQ' },
  { code: 'AFN', symbol: 'AFN ', label: 'Afghan Afghani',       locale: 'fa-AF' },
  // North America
  { code: 'USD', symbol: '$',    label: 'US Dollar',            locale: 'en-US' },
  { code: 'CAD', symbol: 'C$',   label: 'Canadian Dollar',      locale: 'en-CA' },
  { code: 'MXN', symbol: 'MX$',  label: 'Mexican Peso',         locale: 'es-MX' },
  // UK + Europe (west)
  { code: 'GBP', symbol: '£',    label: 'British Pound',        locale: 'en-GB' },
  { code: 'EUR', symbol: '€',    label: 'Euro',                 locale: 'en-IE' },
  { code: 'CHF', symbol: 'CHF ', label: 'Swiss Franc',          locale: 'de-CH' },
  { code: 'SEK', symbol: 'SEK ', label: 'Swedish Krona',        locale: 'sv-SE' },
  { code: 'NOK', symbol: 'NOK ', label: 'Norwegian Krone',      locale: 'nb-NO' },
  { code: 'DKK', symbol: 'DKK ', label: 'Danish Krone',         locale: 'da-DK' },
  { code: 'ISK', symbol: 'ISK ', label: 'Icelandic Króna',      locale: 'is-IS' },
  // Europe (central + east + balkans)
  { code: 'PLN', symbol: 'PLN ', label: 'Polish Złoty',         locale: 'pl-PL' },
  { code: 'CZK', symbol: 'CZK ', label: 'Czech Koruna',         locale: 'cs-CZ' },
  { code: 'HUF', symbol: 'HUF ', label: 'Hungarian Forint',     locale: 'hu-HU' },
  { code: 'RON', symbol: 'RON ', label: 'Romanian Leu',         locale: 'ro-RO' },
  { code: 'BGN', symbol: 'BGN ', label: 'Bulgarian Lev',        locale: 'bg-BG' },
  { code: 'RSD', symbol: 'RSD ', label: 'Serbian Dinar',        locale: 'sr-RS' },
  { code: 'RUB', symbol: '₽',    label: 'Russian Ruble',        locale: 'ru-RU' },
  { code: 'UAH', symbol: '₴',    label: 'Ukrainian Hryvnia',    locale: 'uk-UA' },
  // Central Asia
  { code: 'KZT', symbol: '₸',    label: 'Kazakhstani Tenge',    locale: 'kk-KZ' },
  { code: 'UZS', symbol: 'UZS ', label: 'Uzbekistani Som',      locale: 'uz-UZ' },
  // APAC
  { code: 'SGD', symbol: 'S$',   label: 'Singapore Dollar',     locale: 'en-SG' },
  { code: 'HKD', symbol: 'HK$',  label: 'Hong Kong Dollar',     locale: 'en-HK' },
  { code: 'AUD', symbol: 'A$',   label: 'Australian Dollar',    locale: 'en-AU' },
  { code: 'NZD', symbol: 'NZ$',  label: 'New Zealand Dollar',   locale: 'en-NZ' },
  { code: 'FJD', symbol: 'FJ$',  label: 'Fijian Dollar',        locale: 'en-FJ' },
  { code: 'JPY', symbol: '¥',    label: 'Japanese Yen',         locale: 'ja-JP' },
  { code: 'CNY', symbol: 'CN¥',  label: 'Chinese Yuan',         locale: 'zh-CN' },
  { code: 'KRW', symbol: '₩',    label: 'South Korean Won',     locale: 'ko-KR' },
  { code: 'TWD', symbol: 'NT$',  label: 'Taiwan Dollar',        locale: 'zh-TW' },
  { code: 'THB', symbol: '฿',    label: 'Thai Baht',            locale: 'th-TH' },
  { code: 'MYR', symbol: 'RM',   label: 'Malaysian Ringgit',    locale: 'ms-MY' },
  { code: 'IDR', symbol: 'IDR ', label: 'Indonesian Rupiah',    locale: 'id-ID' },
  { code: 'PHP', symbol: '₱',    label: 'Philippine Peso',      locale: 'en-PH' },
  { code: 'VND', symbol: '₫',    label: 'Vietnamese Đồng',      locale: 'vi-VN' },
  // Africa
  { code: 'ZAR', symbol: 'R',    label: 'South African Rand',   locale: 'en-ZA' },
  { code: 'EGP', symbol: 'EGP ', label: 'Egyptian Pound',       locale: 'ar-EG' },
  { code: 'MAD', symbol: 'MAD ', label: 'Moroccan Dirham',      locale: 'ar-MA' },
  { code: 'TND', symbol: 'TND ', label: 'Tunisian Dinar',       locale: 'ar-TN' },
  { code: 'NGN', symbol: '₦',    label: 'Nigerian Naira',       locale: 'en-NG' },
  { code: 'GHS', symbol: 'GH₵',  label: 'Ghanaian Cedi',        locale: 'en-GH' },
  { code: 'KES', symbol: 'KSh',  label: 'Kenyan Shilling',      locale: 'en-KE' },
  { code: 'TZS', symbol: 'TSh',  label: 'Tanzanian Shilling',   locale: 'en-TZ' },
  { code: 'UGX', symbol: 'USh',  label: 'Ugandan Shilling',     locale: 'en-UG' },
  { code: 'ETB', symbol: 'ETB ', label: 'Ethiopian Birr',       locale: 'en-ET' },
  { code: 'MUR', symbol: '₨',    label: 'Mauritian Rupee',      locale: 'en-MU' },
  // Latin America
  { code: 'BRL', symbol: 'R$',   label: 'Brazilian Real',       locale: 'pt-BR' },
  { code: 'ARS', symbol: 'ARS ', label: 'Argentine Peso',       locale: 'es-AR' },
  { code: 'CLP', symbol: 'CLP$', label: 'Chilean Peso',         locale: 'es-CL' },
  { code: 'COP', symbol: 'COL$', label: 'Colombian Peso',       locale: 'es-CO' },
  { code: 'PEN', symbol: 'S/',   label: 'Peruvian Sol',         locale: 'es-PE' },
  { code: 'UYU', symbol: '$U',   label: 'Uruguayan Peso',       locale: 'es-UY' },
  // Caribbean
  { code: 'JMD', symbol: 'J$',   label: 'Jamaican Dollar',      locale: 'en-JM' },
  { code: 'TTD', symbol: 'TT$',  label: 'Trinidad & Tobago Dollar', locale: 'en-TT' },
];

export const currencySymbol = (code) =>
  (CURRENCIES.find((c) => c.code === code) || CURRENCIES[0]).symbol;

// v3.7.7 Phase 84.31 — Currency picker label helper.
// Some ISO codes (AED, SAR, PKR, LKR, etc.) use the code itself as the
// symbol in our dictionary, so naively rendering "${symbol} ${code}"
// produces "AED AED". This helper collapses that duplication and also
// exposes the full name so pickers can render "AED — UAE Dirham" cleanly.
export function currencyPickerLabel(code, { withName = false } = {}) {
  const c = CURRENCIES.find((x) => x.code === code);
  if (!c) return code || '';
  const symTrim = (c.symbol || '').trim();
  const head = symTrim === c.code ? c.code : `${symTrim} ${c.code}`;
  return withName && c.label ? `${head} — ${c.label}` : head;
}


// Phase 62 — Currency-aware compaction.
// Indian currency uses lakh / crore; everything else uses k / M / B. We
// also enforce sensible rollover thresholds so we never print things like
// "1,000 L" — 100 L always becomes 1 Cr automatically.
const INR_CODES = new Set(['INR']);

export function formatMoney(amount, currency = 'INR', { compact = false, decimals } = {}) {
  const num = Number(amount || 0);
  const sym = currencySymbol(currency);
  const cur = (currency || 'INR').toUpperCase();
  const neg = num < 0;
  const abs = Math.abs(num);

  if (compact) {
    if (INR_CODES.has(cur)) {
      // INR: cap at Cr; below 1L stay numeric so "₹95,000" doesn't compact.
      if (abs >= 1e7) {
        const v = abs / 1e7;
        return `${neg ? '-' : ''}${sym}${v.toFixed(v >= 100 ? 0 : 2)}Cr`;
      }
      if (abs >= 1e5) {
        const v = abs / 1e5;
        return `${neg ? '-' : ''}${sym}${v.toFixed(v >= 100 ? 0 : 2)}L`;
      }
      // fall through to non-compact formatting for <1L INR
    } else {
      // Non-INR: k / M / B. Use 1e4 threshold so $10k+ compacts, but $999 stays full.
      if (abs >= 1e9) return `${neg ? '-' : ''}${sym}${(abs / 1e9).toFixed(2)}B`;
      if (abs >= 1e6) return `${neg ? '-' : ''}${sym}${(abs / 1e6).toFixed(2)}M`;
      if (abs >= 1e4) return `${neg ? '-' : ''}${sym}${(abs / 1e3).toFixed(1)}k`;
      // fall through for <$10k
    }
  }

  const fd = decimals !== undefined ? decimals : (Number.isInteger(num) ? 0 : 2);
  return `${neg ? '-' : ''}${sym}${abs.toLocaleString(undefined, {
    minimumFractionDigits: fd, maximumFractionDigits: fd,
  })}`;
}

export function formatPercent(n, digits = 0) {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0;
  return `${v.toFixed(digits)}%`;
}

export function formatDate(d) {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export function startOfMonthISO(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
